import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Alert, Box, Button, Card, Skeleton, Stack, Typography } from '@mui/material';
import Iconify from '../../iconify/Iconify';

export default function AttendanceSummaryPanel({ rows, month, loading, error, onRetry }) {
  const totals = useMemo(
    () =>
      (rows || []).reduce(
        (acc, row) => {
          acc.present += Number(row.present || 0);
          acc.absent += Number(row.absent || 0);
          acc.halfDay += Number(row.halfDay || 0);
          acc.leave += Number(row.leave || 0);
          acc.holiday += Number(row.holiday || 0);
          acc.payableDays += Number(row.payableDays || 0);
          return acc;
        },
        { present: 0, absent: 0, halfDay: 0, leave: 0, holiday: 0, payableDays: 0 }
      ),
    [rows]
  );

  const chartSeries = [totals.present, totals.absent, totals.halfDay, totals.leave, totals.holiday];
  const chartOptions = {
    labels: ['Present', 'Absent', 'Half Day', 'Leave', 'Holiday'],
    legend: { position: 'bottom', fontSize: '11px' },
    colors: ['#2e7d32', '#d32f2f', '#ed6c02', '#0288d1', '#9e9e9e'],
    dataLabels: { enabled: true },
    plotOptions: { pie: { donut: { size: '66%' } } },
  };

  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 22px rgba(16, 24, 40, 0.05)',
        height: '100%',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Iconify icon="solar:calendar-mark-bold-duotone" width={18} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Attendance Summary
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Month: {month || '-'}
      </Typography>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Failed to load attendance summary.
        </Alert>
      ) : loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      ) : (
        <Stack spacing={1.2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 1 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(46,125,50,0.10)' }}>
              <Typography variant="caption" color="text.secondary">Present</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{totals.present}</Typography>
            </Box>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(211,47,47,0.10)' }}>
              <Typography variant="caption" color="text.secondary">Absent</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{totals.absent}</Typography>
            </Box>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(237,108,2,0.10)' }}>
              <Typography variant="caption" color="text.secondary">Half Day</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{totals.halfDay}</Typography>
            </Box>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(2,136,209,0.10)' }}>
              <Typography variant="caption" color="text.secondary">Payable Days</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{totals.payableDays.toFixed(1)}</Typography>
            </Box>
          </Box>

          <Box dir="ltr">
            <ReactApexChart type="donut" height={220} series={chartSeries} options={chartOptions} />
          </Box>
        </Stack>
      )}
    </Card>
  );
}
